import { COMPLIMENTARY_USAGE_BANNER_MODAL_ID } from '@/features/settings/billing/complimentary-usage-banner-modal-id';
import { type ModalView } from '@/types';
import {
  DEFAULT_LANDING_PAGE_BANNER_MODAL_ID,
  HEIDI_2_CONFIRM_ACCESS_MODAL_ID,
  HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
  HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
  HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
  HEIDI_2_NEW_USER_ONBOARDING_BANNER_MODAL_ID,
} from '@heidi/agents/contracts';
import { ModalId } from '@heidi/schema/ml-scribe';

/**
 * Declarative rules for when a modal should be treated as "closed" — i.e.
 * stop re-appearing to the user. Each rule is evaluated against the backend
 * ModalView record. The modal is closed when ANY of the supplied thresholds
 * is met, or when the optional `predicate` escape hatch returns true.
 *
 * Modal IDs absent from `MODAL_CLOSED_RULES` fall back to legacy behaviour:
 * any entry in `user.seen_modals` counts as "closed". That preserves the
 * one-and-done semantics `useQueuedModal` callers (queued coachmarks,
 * share-session-announce spotlight, etc.) rely on.
 */
export interface ModalClosedRule {
  /** Closed once the user has engaged this many times. */
  engageAtLeast?: number;
  /** Closed once the user has accumulated this many consecutive dismissals. */
  dismissAtLeast?: number;
  /** Closed once the modal has been shown this many times. */
  viewCountAtLeast?: number;
  /** Escape hatch for rules that don't fit the threshold shape. */
  predicate?: (view: ModalView | null | undefined) => boolean;
  /**
   * When set, the closed verdict only applies for `cooldownDays` after
   * `last_seen_at`. After that window passes the rule re-opens, letting the
   * modal show again. Useful for "snooze-style" suppression where a banner
   * should stop nagging after N dismissals but eventually return.
   */
  cooldownDays?: number;
}

/**
 * The Heidi II explore-later nudges close for good on the first dismiss OR
 * engage. A spotlight's "Got it" and the tour's "Show me" are engages, so a
 * dismiss-only rule would bring them back.
 */
const HEIDI_2_NUDGE_CLOSED_RULE: ModalClosedRule = {
  dismissAtLeast: 1,
  engageAtLeast: 1,
};

const SOURCE_RECOMMENDATION_BANNER_RULE: ModalClosedRule = {
  engageAtLeast: 1,
  dismissAtLeast: 1,
  // 4 (not 3) because view_count is incremented on mount while visible: a
  // threshold of 3 causes the 3rd mount to flash and immediately unmount as
  // the optimistic update bumps the count mid-render. 4 gives the user three
  // clear impressions before auto-dismissing.
  viewCountAtLeast: 4,
};

/**
 * Sessionless Evidence awareness — two cooldowns running in parallel against the
 * same `ModalView`; the modal is closed if EITHER rule fires:
 *
 *   1. **Cadence** — 4 day snooze after any recorded interaction. The popover
 *      doesn't bump `view_count` on impression (would flash-then-hide
 *      mid-render — same gotcha as the source rec banner); instead the
 *      existence of any record acts as the anchor. Records are written on
 *      engage/dismiss, the 5-minute auto-dismiss, or as a first-impression
 *      grace anchor for brand-new users (the popover hook writes a passive
 *      `suppress` on first eligible mount so they wait 4 days too).
 *   2. **Long suppression** — 60 day snooze after 3 consecutive dismissals
 *      (X / Escape / 5-min auto-dismiss; floating UI ignores outside click). Engaging the CTA resets
 *      `consecutive_dismiss_count`, so this only fires for repeat ignorers.
 *
 * Both windows evaluate against `last_seen_at`, which the BE updates on every
 * modal-view mutation.
 */
const SESSIONLESS_EVIDENCE_AWARENESS_CADENCE_COOLDOWN_DAYS = 4;
const SESSIONLESS_EVIDENCE_AWARENESS_DISMISS_AT_LEAST = 3;
const SESSIONLESS_EVIDENCE_AWARENESS_DISMISS_COOLDOWN_DAYS = 60;
/** Time the popover stays on screen before auto-dismissing as a non-engagement. */
export const SESSIONLESS_EVIDENCE_AWARENESS_AUTO_DISMISS_MS = 5 * 60 * 1000;
const SESSIONLESS_EVIDENCE_AWARENESS_RULES: ModalClosedRule[] = [
  {
    // Predicate (not `viewCountAtLeast: 1`) so an impression-on-mount bump
    // wouldn't be required — see the long comment above.
    predicate: (view) => view != null,
    cooldownDays: SESSIONLESS_EVIDENCE_AWARENESS_CADENCE_COOLDOWN_DAYS,
  },
  {
    dismissAtLeast: SESSIONLESS_EVIDENCE_AWARENESS_DISMISS_AT_LEAST,
    cooldownDays: SESSIONLESS_EVIDENCE_AWARENESS_DISMISS_COOLDOWN_DAYS,
  },
];

/**
 * In-session document drafting suggestion banner: snooze-style suppression
 * tracked on a single `seen_modals` record. Two thresholds run in parallel
 * against the same `ModalView` and either one quiets the banner for 30 days:
 *
 *   1. **Active dismiss** — `consecutive_dismiss_count >= 2`. Bumped when the
 *      user X's every suggestion in a session; reset to 0 on engagement.
 *   2. **Lifetime non-engagement** — `view_count - engage_count >= 4`. Same
 *      "4 not 3" rationale as the source recommendation banner: a threshold of
 *      3 can flash-then-hide on the third impression when the count updates
 *      mid-render. Counts walkaways promoted via cross-session abandonment in
 *      `useDocumentSuggestionBannerSuppression` (view-only bumps). Dismiss does
 *      not pad `view_count`; `consecutive_dismiss_count` is the active-dismiss signal.
 *
 * Export these numerics so analytics and `recordAllDismissed` use the same
 * boundaries as `evaluateClosedRule` / `isModalClosed`.
 */
export const DOCUMENT_SUGGESTION_BANNER_DISMISS_AT_LEAST = 2;
export const DOCUMENT_SUGGESTION_BANNER_UNENGAGED_GAP_AT_LEAST = 4;
export const DOCUMENT_SUGGESTION_BANNER_COOLDOWN_DAYS = 30;

/** Global suppression after this many consecutive export-banner dismissals. */
export const DOCUMENT_EXPORT_BANNER_DISMISS_AT_LEAST = 3;

/**
 * Lifetime cap on `view_count - engage_count` for the export banner. Dismiss
 * does not increment `view_count` (only mounts / engage / walkaways do), so
 * we use the same "4 not 3" guard as the source recommendation banner: a
 * threshold of 3 would let the 3rd mount flash then immediately unmount when the
 * optimistic increment lands mid-render.
 */
export const DOCUMENT_EXPORT_BANNER_UNENGAGED_GAP_AT_LEAST = 4;

/**
 * Evidence document export banner: suppressed after either
 *   1. `DOCUMENT_EXPORT_BANNER_DISMISS_AT_LEAST` consecutive dismissals, OR
 *   2. `DOCUMENT_EXPORT_BANNER_UNENGAGED_GAP_AT_LEAST` unengaged view deltas.
 *
 * `view_count - engage_count` counts impressions (mount, walkaway padding,
 * engage's +1) while only `engage` bumps `engage_count`; dismiss does not
 * add to `view_count` (see `useMutateMarkModalAsViewed`).
 *
 * Per-consult once-only suppression lives in
 * `docExportBannerClosedConsultSessionIdsAtom` since modal_views isn't
 * session-scoped.
 */
const DOCUMENT_EXPORT_BANNER_RULE: ModalClosedRule = {
  dismissAtLeast: DOCUMENT_EXPORT_BANNER_DISMISS_AT_LEAST,
  predicate: (view) => {
    const views = view?.view_count ?? 0;
    const engages = view?.engage_count ?? 0;
    return views - engages >= DOCUMENT_EXPORT_BANNER_UNENGAGED_GAP_AT_LEAST;
  },
};

const DOCUMENT_SUGGESTION_BANNER_RULE: ModalClosedRule = {
  dismissAtLeast: DOCUMENT_SUGGESTION_BANNER_DISMISS_AT_LEAST,
  predicate: (view) =>
    (view?.view_count ?? 0) - (view?.engage_count ?? 0) >=
    DOCUMENT_SUGGESTION_BANNER_UNENGAGED_GAP_AT_LEAST,
  cooldownDays: DOCUMENT_SUGGESTION_BANNER_COOLDOWN_DAYS,
};

/**
 * Weekly clinician (NPI) verification reminder: a rolling 7-day snooze from
 * the last close, any record being the anchor (as the sessionless-Evidence
 * cadence does). Whether the user still needs reminding is the badge probe's
 * job, not this rule's.
 */
export const HCP_VERIFICATION_REMINDER_COOLDOWN_DAYS = 7;
const HCP_VERIFICATION_REMINDER_RULE: ModalClosedRule = {
  predicate: (view) => view != null,
  cooldownDays: HCP_VERIFICATION_REMINDER_COOLDOWN_DAYS,
};

/**
 * A modal id can register either a single rule or an array of rules. When
 * multiple rules are present they are OR-combined: the modal is closed if
 * **any** rule fires. Arrays are the right shape when the same modal needs
 * different cooldown windows for different triggers (e.g. a short cadence
 * cooldown after each impression plus a long suppression after N dismissals).
 */
export const MODAL_CLOSED_RULES: Partial<
  Record<ModalId, ModalClosedRule | readonly ModalClosedRule[]>
> = {
  // Approved once-per-user discovery, including a viewed but unclosed hint.
  [ModalId.ACTION_COPY_SETTINGS_COACHMARK]: {
    viewCountAtLeast: 1,
    dismissAtLeast: 1,
    engageAtLeast: 1,
  },
  [ModalId.SOURCE_RECOMMENDATION_BANNER]: SOURCE_RECOMMENDATION_BANNER_RULE,
  [ModalId.SOURCE_RECOMMENDATION_APPLY_BANNER]: SOURCE_RECOMMENDATION_BANNER_RULE,
  [ModalId.EVIDENCE_DOCUMENT_EXPORT_BANNER]: DOCUMENT_EXPORT_BANNER_RULE,
  [ModalId.SESSIONLESS_EVIDENCE_AWARENESS]: SESSIONLESS_EVIDENCE_AWARENESS_RULES,
  [ModalId.EVIDENCE_DOCUMENT_SUGGESTION_BANNER]: DOCUMENT_SUGGESTION_BANNER_RULE,
  [ModalId.T4_CUSTOM_AUDIENCE_HEIDI_2_OPT_IN_BANNER]: {
    dismissAtLeast: 1,
  },
  [ModalId.T4_CUSTOM_AUDIENCE_HEIDI_2_REQUEST_ACCESS_BANNER]: {
    dismissAtLeast: 1,
  },
  [HEIDI_2_NEW_USER_ONBOARDING_BANNER_MODAL_ID]: {
    dismissAtLeast: 1,
  },
  [COMPLIMENTARY_USAGE_BANNER_MODAL_ID]: {
    dismissAtLeast: 1,
  },
  [HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID]: HEIDI_2_NUDGE_CLOSED_RULE,
  [HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID]: HEIDI_2_NUDGE_CLOSED_RULE,
  [HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID]: HEIDI_2_NUDGE_CLOSED_RULE,
  // Only Confirm / Manage access (engage) answers the once-only prompt; a
  // dismiss snoozes it for the T4 week rather than spending it.
  [HEIDI_2_CONFIRM_ACCESS_MODAL_ID]: [
    { engageAtLeast: 1 },
    { dismissAtLeast: 1, cooldownDays: 7 },
  ],
  // Manage is an engage and closes it too.
  [DEFAULT_LANDING_PAGE_BANNER_MODAL_ID]: {
    dismissAtLeast: 1,
    engageAtLeast: 1,
  },
  [ModalId.CUSTOM_AUDIENCE_HCP_VERIFICATION_REMINDER_MODAL]:
    HCP_VERIFICATION_REMINDER_RULE,
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Pure evaluator for a single rule. Exported so tests can exercise rule
 * shapes without mutating the shared `MODAL_CLOSED_RULES` map.
 */
export const evaluateClosedRule = (
  rule: ModalClosedRule,
  view: ModalView | null | undefined,
  /** Override `Date.now()` for deterministic tests. */
  now: number = Date.now()
): boolean => {
  // Permanent dismiss short-circuit — bypasses every threshold and cooldown.
  // Set when the user clicks a "Don't show again" affordance; the BE pins
  // `hide_forever: true` on the ModalView and we honor it indefinitely.
  if (view?.hide_forever) return true;

  const thresholdMet = (() => {
    if (rule.predicate?.(view)) return true;
    const engage = view?.engage_count ?? 0;
    const dismiss = view?.consecutive_dismiss_count ?? 0;
    const views = view?.view_count ?? 0;
    if (rule.engageAtLeast !== undefined && engage >= rule.engageAtLeast) return true;
    if (rule.dismissAtLeast !== undefined && dismiss >= rule.dismissAtLeast) return true;
    if (rule.viewCountAtLeast !== undefined && views >= rule.viewCountAtLeast)
      return true;
    return false;
  })();

  if (!thresholdMet) return false;
  if (rule.cooldownDays === undefined) return true;

  // Cooldown applies — the rule's verdict is only "closed" while we're still
  // within `cooldownDays` of the last interaction. After that, it re-opens.
  const lastSeenAt = view?.last_seen_at;
  if (!lastSeenAt) return true;
  const lastSeenMs = Date.parse(lastSeenAt);
  if (Number.isNaN(lastSeenMs)) return true;
  return now < lastSeenMs + rule.cooldownDays * MS_PER_DAY;
};

export const isModalClosed = (
  modalId: ModalId,
  view: ModalView | null | undefined,
  now: number = Date.now()
): boolean => {
  const rule = MODAL_CLOSED_RULES[modalId];
  // No registered rule → fall back to legacy "any seen entry closes the modal".
  if (!rule) return true;
  // OR-combine when multiple rules are registered for one id.
  const rules: readonly ModalClosedRule[] = Array.isArray(rule) ? rule : [rule];
  return rules.some((r) => evaluateClosedRule(r, view, now));
};
